import React, { FC, MouseEvent } from "react";

import CheckIcon from "@mui/icons-material/Check";
import { RectCSS, Word } from "store/types";

interface Rectangle {
  id: string;
  text: string;
  css: RectCSS;
}

interface Props {
  className?: string;
  rectangle: Rectangle;
  anchorCSS?: RectCSS;
  selectedWord: Word | null;
  isHighlighted?: boolean;
  isHovered?: boolean;
  tagNumber?: number;
  isSaved?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>, id: string, text: string) => void;
  onMouseEnter?: (rectId: string) => void;
  onMouseLeave?: () => void;
}

const adjustRectCSS = (anchorCSS: RectCSS, rectCSS: RectCSS): RectCSS => ({
  left: rectCSS.left - anchorCSS.left + 1,
  top: rectCSS.top - anchorCSS.top + 1,
  width: rectCSS.width,
  height: rectCSS.height
});

const getCss = (rectId: string, selectedWord: Word | null, className?: string) => {
  const css = className ? ["word", className] : ["word"];

  if (selectedWord?.rectId === rectId) {
    css.push("word--selected");
  }

  return css.join(" ");
};

export const PDFWordRectangle: FC<Props> = React.memo(
  ({
    className,
    rectangle,
    anchorCSS,
    selectedWord,
    isHighlighted,
    isHovered,
    tagNumber,
    isSaved,
    onClick,
    onMouseEnter,
    onMouseLeave
  }) => {
    const polygonBackground = isHighlighted ? "rgba(24, 129, 0, 0.2)" : "rgba(255, 217, 0, 0.2)";

    const hasTag = tagNumber !== undefined;
    const hoverOutline = isHovered ? { outline: "1px solid #071f35", outlineOffset: 0, zIndex: 2 } : undefined;

    return (
      <div
        key={rectangle.id}
        className={getCss(rectangle.id, selectedWord, className)}
        style={{
          ...(anchorCSS ? adjustRectCSS(anchorCSS, rectangle.css) : rectangle.css),
          background: polygonBackground
        }}
        onClick={(e) => onClick?.(e, rectangle.id, rectangle.text)}
        onMouseEnter={() => onMouseEnter?.(rectangle.id)}
        onMouseLeave={onMouseLeave}
      >
        {hasTag && (
          <span
            style={{
              position: "absolute",
              right: "100%",
              top: 0,
              bottom: 0,
              width: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#188100",
              fontSize: "14px",
              fontWeight: 700,
              color: "white",
              lineHeight: "16px"
            }}
          >
            {tagNumber}
          </span>
        )}
        {isSaved && (
          <CheckIcon
            sx={{
              position: "absolute",
              left: "100%",
              top: 0,
              bottom: 0,
              height: "auto",
              background: "#07a27d33",
              fill: "#07a27d",
              display: "flex",
              alignItems: "center"
            }}
          />
        )}
        {hoverOutline && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 0,
              left: hasTag ? -18 : 0,
              ...(isSaved && { right: -18 }),
              border: "1px solid #071f35",
              pointerEvents: "none",
              zIndex: 2
            }}
          />
        )}
      </div>
    );
  }
);
